import { Directive, ElementRef, HostListener } from '@angular/core';

@Directive({
    selector: '[appPercentageDirective]',
    standalone: false
})
export class PercentageDirective {
  private specialKeys: Array<string> = [
    'Backspace',
    'Tab',
    'End',
    'Home',
    '-',
    'ArrowLeft',
    'ArrowRight',
    'Del',
    'Delete',
    '.',
  ];

  constructor(private el: ElementRef) {}

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    const max = event.srcElement['max'];
    // Allow Backspace, tab, end, and home keys
    if (this.specialKeys.indexOf(event.key) !== -1) {
      return;
    }

    let current: string = this.el.nativeElement.value;
    const position = this.el.nativeElement.selectionStart;
    const next: string = [
      current.slice(0, position),
      event.key,
      current.slice(position),
    ].join('');

    // Don't allow multiple dots
    if (event.key === '.' && current.includes('.')) {
      event.preventDefault();
      return;
    }

    // Don't allow dot as first character
    if (event.key === '.' && current.length === 0) {
      event.preventDefault();
      return;
    }

    const [beforeDecimal, afterDecimal] = next.split('.');

    // Handle numbers before decimal point (max 5 digits)
    if (beforeDecimal && beforeDecimal.length > 5) {
      event.preventDefault();
      return;
    }

    // Handle decimal places (max 2 digits)
    if (afterDecimal && afterDecimal.length > 2) {
      event.preventDefault();
      return;
    }

    // Check if the value would exceed max
    const numericValue = parseFloat(next);
    if (!isNaN(numericValue) && max && numericValue > parseFloat(max)) {
      event.preventDefault();
      return;
    }

    // Only allow numbers and decimal point
    if (!/^\d$/.test(event.key) && event.key !== '.') {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedInput: string = event.clipboardData.getData('text/plain');

    // Remove any non-numeric characters except decimal point
    const cleanedInput = pastedInput.replace(/[^\d.]/g, '');
    const parts = cleanedInput.split('.');

    // Handle multiple decimal points
    if (parts.length > 2) {
      return;
    }

    // Format number with max 2 decimal places
    let formatted = parts[0];
    if (parts.length === 2) {
      formatted += '.' + parts[1].slice(0, 2);
    }

    const numericValue = parseFloat(formatted);
    if (isNaN(numericValue)) {
      return;
    }

    // Ensure max 5 digits before decimal
    if (parts[0].length > 5) {
      formatted = parts[0].slice(0, 5) + (parts[1] ? '.' + parts[1].slice(0, 2) : '');
    }

    const max = this.el.nativeElement.max;
    if (max && numericValue > parseFloat(max)) {
      return;
    }

    document.execCommand('insertText', false, formatted);
  }

  @HostListener('input', ['$event'])
  onInput(event: InputEvent) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Remove any non-numeric characters except decimal point
    value = value.replace(/[^\d.]/g, '');

    // Handle multiple decimal points
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }

    // Limit to 2 decimal places
    if (parts.length === 2) {
      value = parts[0] + '.' + parts[1].slice(0, 2);
    }

    // Limit to 5 digits before decimal
    if (parts[0].length > 5) {
      value = parts[0].slice(0, 5) + (parts[1] ? '.' + parts[1].slice(0, 2) : '');
    }

    input.value = value;
  }
}
